/**
 * `predicate.value_kind_mismatch` (§5.2 Soft-constraint rules) — a fact's
 * value's `kind` doesn't match its predicate's declared `value_kind`.
 *
 * Severity: `warning`. Runs only for CURATED predicates (a declaration in the
 * registry); open-vocabulary predicates carry no declared kind to violate. The
 * check reuses the substrate's own write-boundary validator
 * (`validateValueWithDeclaration`, which also enforces `enum` membership), so a
 * fact that fails here is one that would not pass a fresh write — a real
 * migration / homebrew-edit defect, not noise.
 */
import { currentFactValue, validateValueWithDeclaration } from '@dl/shared';

import { factRef, type RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

import { forEachFact } from './helpers.js';

export function valueKindMismatch(ctx: AuditContext): RawAdvisory[] {
  const out: RawAdvisory[] = [];

  forEachFact(ctx.entities, ({ entity, factKey, predicate, fact }) => {
    const decl = ctx.registry.lookup(predicate);
    if (!decl) return; // open-vocabulary predicate — no declared kind

    const value = currentFactValue(fact);
    const result = validateValueWithDeclaration(decl, value);
    if (result.ok) return;

    out.push({
      severity: 'warning',
      rule: 'predicate.value_kind_mismatch',
      message_key: 'auditor.predicate.value_kind_mismatch',
      params: { predicate, expected: decl.value_kind.join(', ') },
      fact_ids: [factRef(entity.id, factKey)],
      entity_ids: [entity.id],
    });
  });

  return out;
}
